import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DELETE_PHOTOS } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'

interface Props {
  projectId: string
  photoIds: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted?: () => void
  inUseCount?: number
}

const DeletePhotosDialog: React.FC<Props> = ({ photoIds, open, onOpenChange, onDeleted, inUseCount = 0 }) => {
  const { t } = useTranslation()
  const [deletePhotos, { loading }] = useMutation(DELETE_PHOTOS, {
    context: { handlesErrors: true },
    refetchQueries: ['photos', 'photoFolders', 'photoAlbums'],
  })

  const confirm = async () => {
    try {
      await deletePhotos({ variables: { ids: photoIds } })
      onOpenChange(false)
      onDeleted?.()
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Photos_delete_photos_title', { count: photoIds.length })}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('Photos_delete_photos_confirm', { count: photoIds.length })}</p>
        {inUseCount > 0 && <p className="text-sm text-muted-foreground">{t('Photos_delete_in_use', { count: inUseCount })}</p>}
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" disabled={loading} onClick={() => onOpenChange(false)}>{t('Common_cancel')}</Button>
          <Button variant="destructive" disabled={loading || photoIds.length === 0} onClick={confirm}>
            {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Common_delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DeletePhotosDialog
